import { ROUTE_COACHES } from '@/constants/Routes'
import { useMyEntities } from '@/services/coachBenefits'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { CoachClaimsInbox } from '../components/CoachClaimsInbox'
import { CoachProfileEditor } from '../components/CoachProfileEditor'

type Tab = 'claims' | 'profile'

/**
 * The coach's own side of benefits: who has claimed, and what members read.
 *
 * Claims open the page because they are the only part with a deadline attached — a member is
 * waiting on an answer, and a profile can be edited whenever. Profile is settings, and settings do
 * not go first.
 *
 * What a coach offers is deliberately absent. Operations author it centrally through the admin
 * panel, so a coach reads their terms on their own public profile rather than setting them here.
 *
 * Renders for whoever operates an entity, which is almost nobody: `/entities/me` is empty for an
 * ordinary member, and this page says so plainly rather than looking broken.
 */
export const MyCoaching = () => {
  const { t } = useTranslation()
  const { data: entities, isLoading, isError, refetch } = useMyEntities()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [tab, setTab] = useState<Tab>('claims')

  const operated = entities ?? []
  // Falls back to the first rather than forcing a choice: almost every coach operates exactly one
  // profile, and making them pick it before seeing anything would be a step for nothing.
  const entity = operated.find((candidate) => candidate.id === selectedId) ?? operated[0] ?? null

  if (isLoading) {
    return <p className="py-16 text-center text-sm text-white text-opacity-70">{t('coach.loading')}</p>
  }

  if (isError) {
    return (
      <div className="mx-auto mt-10 max-w-3xl rounded-lg bg-white p-8 text-center">
        <p className="text-base font-medium text-gray-800">{t('coach.loadFailedTitle')}</p>
        <p className="mt-1 text-sm text-gray-500">{t('coach.loadFailedDesc')}</p>
        <button className="mt-3 text-sm text-gray-900 underline" onClick={() => refetch()}>
          {t('coach.tryAgain')}
        </button>
      </div>
    )
  }

  if (!entity) {
    return (
      <div className="mx-auto mt-10 max-w-3xl rounded-lg bg-white p-8 text-center">
        <p className="text-base font-medium text-gray-800">{t('coach.notACoachTitle')}</p>
        <p className="mt-1 text-sm text-gray-500">{t('coach.notACoachDesc')}</p>
      </div>
    )
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'claims', label: t('coach.tabClaims') },
    { id: 'profile', label: t('coach.tabProfile') },
  ]

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('coach.title')}</h1>
          <p className="mt-1 text-sm text-white text-opacity-80">{t('coach.subtitle')}</p>
        </div>
        <Link
          to={`${ROUTE_COACHES}/${entity.kind}/${entity.id}`}
          className="text-sm text-white text-opacity-80 underline hover:text-opacity-100"
        >
          {t('coach.viewPublicProfile')}
        </Link>
      </header>

      {/* Only shown to the handful of people who run more than one profile. A select with one
          option is a label that looks like a control. */}
      {operated.length > 1 && (
        <label className="mt-4 block text-xs text-white text-opacity-80">
          <span>{t('coach.whichProfile')}</span>
          <select
            className="mt-1 block rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-900"
            value={entity.id}
            onChange={(e) => setSelectedId(Number(e.target.value))}
          >
            {operated.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.attributes?.headline || candidate.kindLabel}
              </option>
            ))}
          </select>
        </label>
      )}

      {/* A profile that is not live cannot be found or claimed at, which is worth saying at the top
          rather than leaving someone to wonder why their queue is empty. */}
      {entity.status !== 'active' && (
        <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {t('coach.notActive', { status: entity.status })}
        </p>
      )}

      <div
        role="tablist"
        aria-label={t('coach.title')}
        className="mt-5 flex gap-1 border-b border-white border-opacity-20"
      >
        {tabs.map((entry) => (
          <button
            key={entry.id}
            role="tab"
            aria-selected={tab === entry.id}
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
              tab === entry.id
                ? 'border-white font-medium text-white'
                : 'border-transparent text-white text-opacity-60 hover:text-opacity-100'
            }`}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {/* One white surface for whichever tab is showing, so each tab's component can be written
          for a light ground and none of them has to know what the page sits on. */}
      <div className="mt-5 rounded-lg bg-white p-4">
        {tab === 'claims' && <CoachClaimsInbox kind={entity.kind} entityId={entity.id} />}
        {tab === 'profile' && <CoachProfileEditor key={entity.id} entity={entity} />}
      </div>
    </div>
  )
}
